import { useEffect, useId, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Button, Card, List, ListItem, useMediaQuery } from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import AccountTreeRounded from "@mui/icons-material/AccountTreeRounded";
import LayersRounded from "@mui/icons-material/LayersRounded";
import ExtensionRounded from "@mui/icons-material/ExtensionRounded";
import FolderOpenRounded from "@mui/icons-material/FolderOpenRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import RouteRounded from "@mui/icons-material/RouteRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import Inventory2Rounded from "@mui/icons-material/Inventory2Rounded";
import { useTranslation } from "react-i18next";
import { packageTransportText } from "../utils/transportPackageErrors";
import "./CicdAutomations.css";

const EMPTY_AUTOS = { loading: false, data: null, error: null };
const errorText = error => typeof error === "string" ? error : error?.message || "";
const stateTone = state => state === "aktuell" ? "success" : "warning";

function Status({ tone = "neutral", children, ...props }) {
  return <span className={`ca-status ca-status--${tone}`} {...props}>
    <span className="ca-dot" aria-hidden="true" />{children}
  </span>;
}

function VersionCard({ label, children, action, icon, variant }) {
  return <ListItem className="ca-version-item" disableGutters>
    <Card component="article" className={`ca-version-card ca-version-card--${variant}`} elevation={0} aria-label={label}>
      <div className="ca-card-heading">
        <span className="ca-card-icon" aria-hidden="true">{icon}</span>
        <h3>{label}</h3>
      </div>
      <div className="ca-version-value">{children}</div>
      {action && <div className="ca-card-action">{action}</div>}
    </Card>
  </ListItem>;
}

function Workflow({ workflow, primary, t }) {
  const state = workflow.zustand === "active"
    ? t("cicdmonitor:auto.zustandAktiv")
    : workflow.zustand || t("cicdmonitor:zustand.unbekannt");
  return <ListItem disableGutters className={`ca-workflow${primary ? " ca-workflow--primary" : ""}`}>
    <span className="ca-workflow-icon" aria-hidden="true">{primary ? <AccountTreeRounded /> : <DescriptionRounded />}</span>
    <div className="ca-workflow-body">
      {primary && <div className="ca-workflow-role">{t("library.used")}</div>}
      <div className="ca-workflow-name">{workflow.name || workflow.pfad}</div>
      <Status tone={primary ? (workflow.zustand === "active" ? "success" : "warning") : "neutral"}>
        {t("cicdmonitor:auto.zustand", { wert: state })}
        {!primary && <> · {t("cicdmonitor:auto.laeuftNichtMit")}</>}
      </Status>
      {workflow.pfad && <div className="ca-path"><FolderOpenRounded aria-hidden="true" /><span>{workflow.pfad}</span></div>}
    </div>
    {workflow.htmlUrl && <Button className="ca-button ca-link" component="a" href={workflow.htmlUrl}
      target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewRounded />}
      aria-label={t("workflow.open", { name: workflow.name || workflow.pfad })}>
      {t("cicdmonitor:auto.inGithubOeffnen")}
    </Button>}
  </ListItem>;
}

/** Presentation only. Loading, mutations and the SAP switch confirmation belong to the parent. */
export default function CicdAutomations({ spaceId, ...props }) {
  return <AutomationsForSpace key={spaceId || "no-space"} {...props} />;
}

function AutomationsForSpace({
  autos = EMPTY_AUTOS, updData, updLoading = false, updError, updMsg,
  canManage = false, activeCount = 0, sapUpdating = false, extUpdating = false,
  rueckbauBusy = false, onCheckUpdates, onUpdateSap, onUpdateExtension, onSwitchToSap,
  deferred = false, onExpand, forceExpanded = false, openRequest = 0, children,
}) {
  const { t, i18n } = useTranslation(["cicdautomations", "cicdmonitor", "gemeinsam"]);
  const id = useId();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const data = autos?.data;
  const workflows = Array.isArray(data?.aktiv) ? data.aktiv : [];
  const templates = Array.isArray(data?.vorlagen) ? data.vorlagen : [];
  const main = workflows.find(workflow => workflow.wirdGefahren);
  const others = workflows.filter(workflow => workflow !== main);
  const workflowError = errorText(autos?.error || data?.fehler);
  const versionError = errorText(updError);
  const sapMode = updData?.transportModus === "sap";
  const configured = updData?.configured === true;
  const sap = updData?.sapBasis;
  const extension = updData?.erweiterung;
  // Missing version identifiers cannot substantiate an "up to date" claim.
  const sapState = sap?.zustand === "aktuell" && !sap.installedShortSha ? "unbekannt" : sap?.zustand || "unbekannt";
  const extState = extension?.zustand === "aktuell" && extension.installedVersion == null ? "unbekannt" : extension?.zustand || "unbekannt";
  // Pakettransport (Welle 6): „fehlt“ heißt Synchronisierung nötig. Reiner Hinweis, er sperrt nichts; der bestehende
  // Knopf der Erweiterung schreibt die Dateien mit.
  const packageSetupMissing = !sapMode && updData?.paketTransport?.zustand === "fehlt";
  // Woher SAPs Workflows für ganze Pakete kommen: zentrales Repository (GIT_CICD_ORGREPO) oder Kopie im Actions Repository.
  const sapWorkflowSource = sapMode ? null : updData?.paketTransport?.sapWorkflowQuelle;
  const sapWorkflowSourceText = sapWorkflowSource?.modus === "zentral" && sapWorkflowSource.repo
    ? packageTransportText("sapWorkflowsCentral", i18n?.language, { repo: sapWorkflowSource.repo, ref: sapWorkflowSource.ref || "" })
    : sapWorkflowSource?.modus === "kopie" ? packageTransportText("sapWorkflowsCopy", i18n?.language) : null;
  // Modus Kopie: SAP-Dateien, die weder dem freigegebenen Stand noch dessen alter Kopie entsprechen (Synchronisieren überschreibt sie nie).
  const divergedFiles = sapMode ? [] : (Array.isArray(updData?.paketTransport?.dateien) ? updData.paketTransport.dateien : [])
    .filter(file => file?.abweichend === true && file.pfad);
  const sapApplicable = sap?.applicable === true;
  const versionsCurrent = configured && !sapMode && sapApplicable && sapState === "aktuell" && extState === "aktuell";
  const workflowsKnown = data?.configured !== false && Array.isArray(data?.aktiv) && Array.isArray(data?.vorlagen);
  const workflowProblem = !workflowsKnown ? "unknown" : !main ? "missing" : main.zustand !== "active" ? `state:${main.zustand}` : null;
  const messageError = updMsg?.type === "bad";
  const healthy = versionsCurrent && !versionError && !workflowError && !messageError && !workflowProblem && !packageSetupMissing;

  // Retain each source's last observation while it reloads. Identical poll results
  // must not reopen a section the user has just closed.
  const observations = JSON.stringify({
    versions: versionError ? [`error:${versionError}`] : updLoading ? null
      : !configured ? ["unknown"] : sapMode ? ["sap-unchecked"]
        : [!sapApplicable ? "sap-unmanaged" : sapState !== "aktuell" ? `sap:${sapState}:${sap?.latestShortSha || sap?.freigegebenShortSha || ""}` : null,
          extState !== "aktuell" ? `extension:${extState}:${extension?.aktuelleVersion || ""}` : null,
          packageSetupMissing ? "package-transport:fehlt" : null].filter(Boolean),
    workflows: workflowError ? [`error:${workflowError}`] : autos?.loading ? null : workflowProblem ? [workflowProblem] : [],
    message: messageError ? [updMsg.text] : [],
  });
  const [disclosure, setDisclosure] = useState(() => ({
    expanded: !deferred && !healthy, manual: false, observed: JSON.parse(observations),
  }));
  const [templatesExpanded, setTemplatesExpanded] = useState(false);
  useEffect(() => {
    if (forceExpanded) setDisclosure(previous => ({ ...previous, expanded: true, manual: true }));
  }, [forceExpanded, openRequest]);
  useEffect(() => {
    if (deferred) return;
    const next = JSON.parse(observations);
    setDisclosure(previous => {
      const observed = { ...previous.observed };
      let newProblem = false;
      Object.entries(next).forEach(([source, problems]) => {
        if (problems === null) return;
        if (problems.some(problem => !previous.observed[source]?.includes(problem))) newProblem = true;
        observed[source] = problems;
      });
      return { observed, manual: previous.manual, expanded: newProblem ? true : previous.manual ? previous.expanded : !healthy };
    });
  }, [observations, healthy, deferred]);

  const busy = sapUpdating || extUpdating || rueckbauBusy;
  const showVersions = configured && !versionError && !updLoading;
  let summary = "summary.unknown";
  let summaryTone = "warning";
  if (deferred && !disclosure.manual && !forceExpanded) {
    summary = "summary.onDemand"; summaryTone = "neutral";
  } else if (versionError || workflowError || messageError) {
    summary = "summary.error";
    summaryTone = "critical";
  } else if (updLoading || autos?.loading) {
    summary = "summary.loading";
    summaryTone = "neutral";
  } else if (sapMode) {
    summary = "summary.sapMode";
    summaryTone = "neutral";
  } else if (sapState === "ungeprueft") summary = "summary.unapproved";
  else if (sapState === "veraltet" || extState === "veraltet" || extState === "fehlt") summary = "summary.update";
  else if (versionsCurrent && workflowProblem) {
    summary = workflowProblem === "unknown" ? "summary.workflowUnknown" : "summary.workflow";
  }
  else if (healthy) {
    summary = "summary.current";
    summaryTone = "success";
  }

  function sapStatus() {
    if (sapMode) return <Status>{t("version.sapNotChecked")}</Status>;
    if (sap?.applicable === false) return <Status>{t("version.sapNotManaged")}</Status>;
    return <>
      <Status tone={stateTone(sapState)}>
        {sapState === "aktuell" ? t("cicdmonitor:version.aktuell")
          : sapState === "veraltet" ? t("cicdmonitor:version.aktualisierungVerfuegbar")
            : sapState === "ungeprueft" ? t("version.sapUnapproved")
              : sapState === "fehlt" ? t("version.sapMissing") : t("cicdmonitor:version.standUnbekannt")}
      </Status>
      {sap?.installedShortSha && <span className="ca-meta">{t("cicdmonitor:version.standSha", { sha: sap.installedShortSha })}</span>}
      {sapState === "veraltet" && (sap?.freigegebenShortSha || sap?.latestShortSha) &&
        <span className="ca-meta">{t("version.approvedSha", { sha: sap.freigegebenShortSha || sap.latestShortSha })}</span>}
      {sapState === "ungeprueft" && <div className="ca-note">
        {sap?.latestShortSha && <span>{t("cicdmonitor:version.ungeprueft", { sha: sap.latestShortSha })} </span>}
        <span id={`${id}-unapproved`}>{t("cicdmonitor:version.ungeprueftHinweis")}</span>
      </div>}
    </>;
  }

  function extensionStatus() {
    if (sapMode) return <Status>{t("version.extensionDeselected")}</Status>;
    return <><Status tone={stateTone(extState)}>
      {extState === "aktuell" ? t("cicdmonitor:version.erwAktuell", { version: extension.installedVersion })
        : extState === "veraltet" && extension?.installedVersion != null && extension?.aktuelleVersion != null
          ? t("cicdmonitor:version.erwVerfuegbar", { version: extension.installedVersion, neu: extension.aktuelleVersion })
          : extState === "veraltet" ? t("version.extensionUpdate")
            : extState === "fehlt" ? t("cicdmonitor:version.erwFehlt") : t("cicdmonitor:version.standUnbekannt")}
    </Status>
      {extState === "veraltet" && (extension?.installedVersion == null || extension?.aktuelleVersion == null) && <>
        {extension?.installedVersion != null && <span className="ca-meta">{t("version.installedVersion", { version: extension.installedVersion })}</span>}
        {extension?.aktuelleVersion != null && <span className="ca-meta">{t("version.availableVersion", { version: extension.aktuelleVersion })}</span>}
      </>}
    </>;
  }

  return <Accordion className="iio ca-root" disableGutters elevation={0} expanded={disclosure.expanded}
    onChange={(_, expanded) => { setDisclosure(previous => ({ ...previous, expanded, manual: true })); if (expanded) onExpand?.(); }}
    TransitionProps={{ timeout: reducedMotion ? 0 : 180, mountOnEnter: deferred }}>
    <AccordionSummary className="ca-summary" expandIcon={<ExpandMoreRounded />}
      id={`${id}-summary`} aria-controls={`${id}-content`} aria-label={t("cicdmonitor:auto.titel")}
      aria-describedby={`${id}-status`}>
      <span className="ca-summary-icon" aria-hidden="true"><Inventory2Rounded /></span>
      <span className="ca-summary-content">
        <span className="ca-eyebrow">{t("workspace.eyebrow")}</span>
        <span className="ca-title" role="heading" aria-level={2}>{t("cicdmonitor:auto.titel")}</span>
        {data?.contentBranch && <span className="ca-branch"><AccountTreeRounded aria-hidden="true" />{t("cicdmonitor:auto.branch", { name: data.contentBranch })}</span>}
      </span>
      <Status tone={summaryTone} id={`${id}-status`}>{t(summary)}</Status>
    </AccordionSummary>
    <AccordionDetails className="ca-content">
      <section className="ca-engine" aria-labelledby={`${id}-engine`}>
      <div className="ca-toolbar">
        <div className="ca-section-heading"><h3 id={`${id}-engine`}>{t("workspace.engine")}</h3><p className="ca-intro">{t("intro")}</p></div>
        {(!sapMode || versionError) && <Button className="ca-button ca-button--solid" variant="contained" disableElevation
          startIcon={<RefreshRounded />} onClick={() => onCheckUpdates?.()}
          disabled={busy || updLoading || !onCheckUpdates}>
          {updLoading ? t("cicdmonitor:version.laedt") : t("cicdmonitor:version.suchen")}
        </Button>}
      </div>

      {versionError && <p className="ca-message" role="alert"><Status tone="critical">{t("version.error", { reason: versionError })}</Status></p>}
      {updLoading && <p className="ca-note" role="status">{t("cicdmonitor:version.laedt")}</p>}
      {!updLoading && !versionError && !configured && <p className="ca-note"><Status tone="warning">{t("version.unavailable")}</Status></p>}

      {showVersions && <>
        <List disablePadding aria-label={t("version.title")} className="ca-versions">
          <VersionCard label={t("cicdmonitor:version.sapBasis")} icon={<LayersRounded />} variant="sap"
            action={!sapMode && sapApplicable && canManage && (sapState === "veraltet" || sapState === "ungeprueft") &&
            <Button className="ca-button" variant="outlined" onClick={() => onUpdateSap?.()}
              disabled={busy || sapState === "ungeprueft" || !onUpdateSap}
              aria-describedby={sapState === "ungeprueft" ? `${id}-unapproved` : undefined}
              aria-label={t("version.updateSap")}>
              {sapUpdating ? t("version.updating") : t("gemeinsam:aktion.aktualisieren")}
            </Button>}>
            {sapStatus()}
          </VersionCard>
          <VersionCard label={t("cicdmonitor:version.erweiterung")} icon={<ExtensionRounded />} variant="extension"
            action={!sapMode && canManage && (["veraltet", "fehlt"].includes(extState) || packageSetupMissing) &&
            <Button className="ca-button" variant="outlined" onClick={() => onUpdateExtension?.()}
              disabled={busy || !onUpdateExtension} aria-label={t(extState === "fehlt" ? "version.installExtension" : "version.updateExtension")}>
              {extUpdating ? t("version.updating") : t(extState === "fehlt" ? "cicdmonitor:version.installieren" : "gemeinsam:aktion.aktualisieren")}
            </Button>}>
            {extensionStatus()}
          </VersionCard>
          <ListItem className="ca-mode" disableGutters>
            <span className="ca-mode-icon" aria-hidden="true"><RouteRounded /></span>
            <div className="ca-mode-content">
              <h3>{t("cicdmonitor:modus.titel")}</h3>
              <span className="ca-mode-value">{t(sapMode ? "cicdmonitor:modus.sap" : "cicdmonitor:modus.erweitert")}</span>
            </div>
            {canManage && <div className="ca-mode-action">{sapMode ? <Button className="ca-button" variant="outlined" onClick={() => onUpdateExtension?.()}
              disabled={busy || !onUpdateExtension}>
              {extUpdating ? t("version.updating") : t("cicdmonitor:modus.aufErweitertWechseln")}
            </Button> : activeCount > 0 ? <span className="ca-note">{t("cicdmonitor:modus.gesperrt")}</span>
              : <Button className="ca-button" variant="outlined" onClick={() => onSwitchToSap?.()}
                disabled={busy || !onSwitchToSap}>
                {rueckbauBusy ? t("cicdmonitor:modus.rueckbauLaeuft") : t("cicdmonitor:modus.aufSapWechseln")}
              </Button>}</div>}
          </ListItem>
        </List>
        {packageSetupMissing && <p className="ca-note" role="status">{packageTransportText("setupMissing", i18n?.language)}</p>}
        {sapWorkflowSourceText && <p className="ca-note">{sapWorkflowSourceText}</p>}
        {divergedFiles.map(file => <p key={`${file.repo || ""}:${file.pfad}`} className="ca-note" role="status" style={{ color: "var(--iio-warning)" }}>
          {packageTransportText("fileDiverged", i18n?.language, { pfad: file.pfad })}</p>)}
        {!sapMode && <p className="ca-note">{t("cicdmonitor:version.hinweis")}</p>}
      </>}

      {updMsg?.text && <p className="ca-message" role={messageError ? "alert" : "status"}>
        <Status tone={messageError ? "critical" : "success"}>{updMsg.text}</Status>
      </p>}
      {Array.isArray(updMsg?.packageFiles) && updMsg.packageFiles.length > 0 && <div className="ca-message" role="status">
        <p className="ca-note">{packageTransportText("setupWritten", i18n?.language)}</p>
        <ul className="ca-note">{updMsg.packageFiles.map(file => <li key={`${file.repo}:${file.pfad}`}>{file.repo}: {file.pfad}</li>)}</ul>
      </div>}
      </section>
      <section className="ca-library" aria-labelledby={`${id}-library`}>
      <div className="ca-library-heading">
        <span className="ca-library-icon" aria-hidden="true"><AccountTreeRounded /></span>
        <div className="ca-section-heading"><h3 id={`${id}-library`}>{t("library.title")}</h3><p className="ca-intro">{t("library.intro")}</p></div>
      </div>
      {workflowError ? <p className="ca-message" role="alert">
        <Status tone="critical">{t(autos?.error ? "workflow.error" : "workflow.deliveryError", { reason: workflowError })}</Status>
      </p> : autos?.loading ? <p className="ca-note" role="status">{t("cicdmonitor:auto.laedt")}</p>
        : !workflowsKnown ? <p className="ca-note"><Status tone="warning">{t("workflow.unknown")}</Status></p>
          : <>
            {main ? <>
              <List disablePadding aria-label={t("workflow.active")}><Workflow workflow={main} primary t={t} /></List>
            </> : <p className="ca-note"><Status tone="warning">{t("workflow.missing")}</Status></p>}
            {!workflows.length && !templates.length && <div className="ca-empty">
              <strong>{t("cicdmonitor:auto.keineWorkflowsTitel")}</strong>
              <p>{t("cicdmonitor:auto.keineWorkflowsText")}</p>
            </div>}
            {others.length > 0 && <>
              <h4 className="ca-subtitle">{t("cicdmonitor:auto.weitereWorkflows")}</h4>
              <List className="ca-other-workflows" disablePadding aria-label={t("cicdmonitor:auto.weitereWorkflows")}>
                {others.map(workflow => <Workflow key={workflow.pfad || workflow.name} workflow={workflow} t={t} />)}
              </List>
            </>}
            {templates.length > 0 && <Accordion className="ca-templates" disableGutters elevation={0}
              expanded={templatesExpanded} onChange={(_, expanded) => setTemplatesExpanded(expanded)}
              TransitionProps={{ timeout: reducedMotion ? 0 : 180 }}>
              <AccordionSummary className="ca-template-summary" expandIcon={<ExpandMoreRounded />}
                id={`${id}-templates-summary`} aria-controls={`${id}-templates-content`}
                aria-label={t("templates.count", { count: templates.length })}>
                <span className="ca-template-icon" aria-hidden="true"><FolderOpenRounded /></span>
                <span className="ca-template-title"><span>{t("templates.count", { count: templates.length })}</span><span className="ca-note">{t("library.templatesHint")}</span></span>
              </AccordionSummary>
              <AccordionDetails className="ca-template-content">
                <p className="ca-note">{t("cicdmonitor:auto.sapVorlagen")}</p>
                <List className="ca-template-grid" disablePadding aria-label={t("templates.count", { count: templates.length })}>
                  {templates.map(template => <ListItem key={template.pfad || template.name} className="ca-template" disableGutters>
                    <DescriptionRounded className="ca-template-file" aria-hidden="true" />
                    <div className="ca-template-body">
                    <div className="ca-workflow-name">{template.name}</div>
                    {template.zweck && <div className="ca-note">{template.zweck}</div>}
                    {template.status && <div className="ca-path">{template.status}</div>}
                    {template.pfad && template.pfad !== template.status && <div className="ca-path">{template.pfad}</div>}
                    </div>
                  </ListItem>)}
                </List>
              </AccordionDetails>
            </Accordion>}
          </>}
      </section>
      {children}
    </AccordionDetails>
  </Accordion>;
}
